import Attrs from '../models/Attrs'
import { generateElementSelectors } from '../styles/selectors'
import replaceStorageURLsFromFirebaseToCloudflare from '../helpers/replaceStorageURLsFromFirebaseToCloudflare'
import renderTemplate from '../helpers/templating'
import { addLanguageController, getBaseBuilderVersion } from '../helpers/utils'

import type { FileUpload, Flow, FlowPage } from '../models/types'

const CONTROLLER = 'components--file-upload'

export default function renderFileUpload(
  component: FileUpload,
  attrs: Attrs,
  page: FlowPage,
  flow: Flow,
  editingMode: boolean,
  userData?: { [key: string]: unknown }
): string {
  const componentVersion = component.component_version ?? 1

  const controllerWithVersion =
    componentVersion >= 2 ? `${CONTROLLER}-v${componentVersion}` : CONTROLLER

  attrs
    .addController(controllerWithVersion)
    .addAction(
      `${controllerWithVersion}:update->flow#update`,
      `${controllerWithVersion}:actions->flow#dispatchAction`
    )

  if (component.show_validation_messages) {
    attrs.addValue(controllerWithVersion, 'validation', `${component.show_validation_messages}`)
  }
  if (flow.disable_form_validation) attrs.add('custom-validity', `true`)

  const isIconSVGorURL = (icon: String | undefined) => {
    if (icon?.startsWith('http')) return 'url'
    if (icon?.startsWith('<svg')) return 'svg'
    return false
  }

  const iconWithURLReplacement = replaceStorageURLsFromFirebaseToCloudflare(component.icon)

  const icon =
    isIconSVGorURL(iconWithURLReplacement) === 'url'
      ? `<img src="${iconWithURLReplacement}" />`
      : isIconSVGorURL(iconWithURLReplacement) === 'svg'
      ? iconWithURLReplacement
      : `<svg ${new Attrs(attrs.static_render).addIcon('fa-solid', 'upload')}></svg>`

  const iconHolderAttrs = new Attrs(attrs.static_render).addDOMClass(
    'flex',
    'flex-row',
    'justify-center',
    'ant-upload-drag-icon'
  )

  const iconHolder = `<p ${iconHolderAttrs}>${icon}</p>`
  const labelText = component.label ? `${component.label}` : 'Click this area to upload'

  const sublabelText = component.sublabel
    ? `${component.sublabel}`
    : 'Only the last file uploaded will be saved'

  const labelAttrs = new Attrs(attrs.static_render).addTarget(controllerWithVersion, 'label')
  const sublabelAttrs = new Attrs(attrs.static_render).addTarget(controllerWithVersion, 'sublabel')

  //* Add language controller for the label and sublabel
  addLanguageController({ attrs: labelAttrs, component, keys: ['label'] })
  addLanguageController({ attrs: sublabelAttrs, component, keys: ['sublabel'] })

  const label = `<p class="file-upload-text" ${labelAttrs}>${labelText}</p>`
  const sublabel = `<p class="file-upload-hint" ${sublabelAttrs}>${sublabelText}</p>`

  const uploaderName = `uploader_${component.key}`

  const uploaderInnerTextBeforeTemplate = `${iconHolder}${label}${sublabel}`
  const uploaderInnerTextAfterTemplate =
    renderTemplate(uploaderInnerTextBeforeTemplate, userData) || ''

  const uploaderAttrs = new Attrs(attrs.static_render)
    .add('for', uploaderName)
    .addDOMClass('uploader')
    .addTarget(controllerWithVersion, 'uploader')

  //* Add template controller if the text has {{
  if (uploaderInnerTextBeforeTemplate.includes('{{'))
    uploaderAttrs
      .addController('template')
      .addValueIfExists(
        'template',
        'text',
        encodeURIComponent(uploaderInnerTextBeforeTemplate || '')
      )
      .addValueIfExists('template', 'always-rerender', component.always_rerender)

  const inputAttrs = new Attrs(attrs.static_render)
    .add('id', uploaderName)
    .add('name', uploaderName)
    .add('type', 'file')
    .add('style', 'opacity:0;')
    .addTarget(controllerWithVersion, 'input')
    .addAction(`change->${controllerWithVersion}#uploadFile`)
    .add('accept', getAcceptTypes(component) || '*')
    .add('tabindex', '-1')

  if (componentVersion >= 2 && component.multiple) inputAttrs.add('multiple', '')

  const fileListAttrs = new Attrs(attrs.static_render)
    .addDOMClass('file-list')
    .addTarget(controllerWithVersion, 'filelist')
  const input = `<input ${inputAttrs}>`
  const uploader = `<label ${uploaderAttrs}>${uploaderInnerTextAfterTemplate}</label>`
  const fileList = `<div ${fileListAttrs}></div>`

  const builderVersion = getBaseBuilderVersion(flow.builder_version)

  const uploaderWrapperAttrs = new Attrs(attrs.static_render)
    .addDOMClass(
      ...generateElementSelectors({
        elementType: 'Uploader',
        component,
        page,
        builderVersion,
      })
    )
    .addAction(`keydown->${controllerWithVersion}#accessibilityFileUpload`)
    .add('tabindex', '0')

  const uploaderWrapper = `<div ${uploaderWrapperAttrs}>${uploader}${input}</div>`
  return `<div ${attrs}>${uploaderWrapper}${fileList}</div>`
}

function getAcceptTypes(component: FileUpload) {
  switch (component.upload_type) {
    case 'all':
      return '*'
    case 'image':
      return 'image/*'
    case 'audio':
      return 'audio/*'
    case 'video':
      return 'video/*'
    case 'doc':
      return '.pdf,.doc,.docx'
    case 'custom':
      return component.custom_upload_type || undefined

    default:
      return undefined
  }
}
